import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Trash, Users } from "lucide-react";
import { USERGROUP } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";

const UserGroup = () => {
    const { create, update } = useApiMethods(); // Added remove for delete operations
    const [userGroupsData, setUserGroupsData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null); // Track which row to delete
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    let isSuperAdmin = JSON.parse(sessionStorage.getItem('is_super_admin'))
    const { add: addPermission, draft: draftPermission, edit: editPermission } = checkUserPermissionFunc("administrator", "User Group", "SubMenu") || {};

    // form state
    const [formData, setFormData] = useState({
        id: null,
        code: "",
        user_group_name: "",
        notes: "",
        is_active: true,
        is_draft: false,
    });

    // reset form & state
    const resetForm = () => {
        setFormData({ id: null, code: "", user_group_name: "", notes: "", is_active: true, is_draft: false });
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // table columns
    const userGroupColumns = [
        { accessor: "code", Header: "User Group Code", sortable: true },
        {
            accessor: "user_group_name",
            Header: "User Group Name",
            sortable: true,
            filterable: true,
        },
        {
            accessor: "notes",
            Header: "Description",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description")
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(
            editPermission || isSuperAdmin ? [
                {
                    accessor: "action",
                    Header: "Actions",
                    sortable: false,
                    Cell: ({ row }) => {
                        const handleOpenDialog = () => {
                            setEditMode(true);
                            setEditId(row?.code);
                            setFormData({
                                id: row?.id || null,
                                code: row?.code || "",
                                user_group_name: row?.user_group_name || "",
                                notes: row?.notes || "",
                                is_active: row?.is_active ?? true,
                                is_draft: !!row?.is_draft,
                            });
                            setOpenDialog(true);
                        };

                        const handleDeleteClick = (row) => {
                            setSelectedRow(row);
                            setIsDeleteConfirmOpen(true);
                        };

                        return (
                            <div className="flex items-center gap-2">
                                {/* Edit Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                    onClick={handleOpenDialog}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>

                                {/* Delete Button (only if row is draft) */}
                                {row?.is_draft && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                        onClick={() => handleDeleteClick(row)}
                                    >
                                        <Trash className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        );
                    },
                },
            ] : [

            ]
        )
    ];

    // save as draft
    const handleDraft = async () => {
        try {
            if (!String(formData.user_group_name || "").trim()) {
                ToastMsg("error", "Please enter the user group name");
                return;
            }
            setLoading(true);
            const payload = { ...formData, is_draft: true };
            const response = (editMode && (editId || formData.id))
                ? await update(USERGROUP, payload)
                : await create(USERGROUP, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "User group saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            if (!selectedRow?.id) {
                ToastMsg("error", "Draft id is missing");
                return;
            }
            const payload = {
                id: selectedRow.id,
                draft_delete: true,
            };
            const response = await update(USERGROUP, payload, false, setLoading);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft deleted successfully");
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft");
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setIsDeleteConfirmOpen(false);
        }
    };

    // save or update
    const handleSave = async () => {
        try {
            if (!String(formData.user_group_name || "").trim()) {
                ToastMsg("error", "Please enter the user group name");
                return;
            }
            setLoading(true);
            let payload = { ...formData, is_draft: false };
            let response;

            if (editMode && editId) {
                // For update, use the specific endpoint with ID
                response = await update(USERGROUP, payload);
            } else {
                response = await create(USERGROUP, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `User group ${editMode ? 'updated' : 'saved'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'save'} user group`);
            }
        } catch (error) {
            console.error("Error saving user group:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} user group`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContainerCard>
            {/* Header */}
            <ModuleTitleCard Icon={Users} title={'User Groups'} subtitle="Organize users into groups with defined access levels" totalRecords={totalRecords} buttonEnable={!!addPermission} setOpenDialog={setOpenDialog} buttonName={'Add User Group'} />

            <Reactable
                columns={userGroupColumns}
                data={userGroupsData}
                setData={setUserGroupsData}
                setTotalCount={setTotalRecords}
                url={USERGROUP}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission || isSuperAdmin ? "sm" : undefined}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add/Edit Modal */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit User Group" : "Add User Group"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                allowDraftInEdit={!!formData.is_draft}
                loading={loading}
                width="400px"
            >
                <form className="space-y-4">
                    {/* Group Name */}
                    <div className="space-y-2">
                        <label
                            htmlFor="groupName"
                            className={cn(modalFieldLabelClass, "flex items-center gap-1")}
                        >
                            Group Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="groupName"
                            value={formData.user_group_name}
                            onChange={(e) =>
                                setFormData({ ...formData, user_group_name: e.target.value })
                            }
                            placeholder="Enter group name"
                            required
                            disabled={loading}
                            className={modalInputClass}
                        />
                    </div>

                    {/* Group Description */}
                    <div className="space-y-2">
                        <label
                            htmlFor="groupDescription"
                            className={modalFieldLabelClass}
                        >
                            Group Description
                        </label>
                        <Textarea
                            id="groupDescription"
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                            placeholder="Enter group description"
                            disabled={loading}
                            {...modalDescriptionFieldProps}
                            className={modalTextareaClass}
                        />
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label
                            htmlFor="is_active"
                            className={modalFieldLabelClass}
                        >
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading || !editMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading || !editMode}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="text-red-500">*</span> indicates required field
                    </p>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft Group"
                description="Are you sure you want to delete this draft group? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default UserGroup;